import { createRef, FC, MouseEvent, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { columnToChars } from '../../_utils';

import { notifyException } from 'services/appsignal';
import { cn } from 'utils/cn';
import { cancelTableSelection } from '../../_actions';
import {
  getAvailablePopupPositions,
  getIsSelectedCellInSelectedRange,
  getSelectedCell
} from '../../_selectors';
import EditExcludedPopup from './EditExcludedPopup';
import ExcludeSelectedPopup from './ExcludeSelectedPopup';

interface IPopup {
  availablePopupPositions: string[];
  selectedCell: { row: number; column: number };
  isSelectedCellInSelectedRange: boolean;
  cancelTableSelection: () => void;
}

const Popup: FC<IPopup> = ({
  isSelectedCellInSelectedRange,
  cancelTableSelection,
  availablePopupPositions,
  selectedCell
}) => {
  const [popupStyle, setPopupStyle] = useState<{ left: number; top: number } | null>(null);
  const [position, setPosition] = useState('up');

  const popupRef = createRef<HTMLDivElement>();

  const popupBoundingRectHeight = popupRef?.current?.getBoundingClientRect()?.height ?? 0;

  const prefix = 'ordering_form__table__popup';

  const handleCancelTableSelection = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      cancelTableSelection();
    },
    [cancelTableSelection]
  );

  const placePopup = useCallback(() => {
    if (typeof window !== 'undefined') {
      const cellElement = document.getElementById(
        `cell-${columnToChars(selectedCell.column)}-${selectedCell.row}`
      );

      if (!cellElement) {
        const errorMsg = `The cell with id cell-${columnToChars(selectedCell.column)}-${
          selectedCell.row
        } where the popup should appear could not be found`;
        return notifyException(errorMsg);
      }

      const {
        offsetTop: top,
        offsetLeft: left,
        offsetWidth: width,
        offsetHeight: height
      } = cellElement;

      let popupStyleValue = null;
      let positionValue = 'right';

      if (!availablePopupPositions.length || availablePopupPositions.includes('right')) {
        popupStyleValue = { left: left + width, top };
      } else if (availablePopupPositions.includes('bottom')) {
        popupStyleValue = { left, top: top + height };
        positionValue = 'bottom';
      } else {
        // Only remaining possibility is for availablePopupPositions to include top
        popupStyleValue = { left, top: top - popupBoundingRectHeight };
        positionValue = 'top';
      }

      setPopupStyle(popupStyleValue);
      setPosition(positionValue);
      scrollTo(0, top - 200);
    }
  }, [availablePopupPositions, popupBoundingRectHeight, selectedCell.column, selectedCell.row]);

  useEffect(() => {
    placePopup();
  }, [placePopup]);

  return (
    <div
      className={cn(prefix, !popupStyle ? 'opacity-0' : '')}
      ref={popupRef}
      style={popupStyle ? popupStyle : {}}
      onClick={handleCancelTableSelection}
    >
      <span className={`${prefix}__arrow ${prefix}__arrow--${position}`} />
      {isSelectedCellInSelectedRange ? <ExcludeSelectedPopup /> : <EditExcludedPopup />}
    </div>
  );
};

export default connect(
  (state) => ({
    availablePopupPositions: getAvailablePopupPositions(state),
    selectedCell: getSelectedCell(state),
    isSelectedCellInSelectedRange: getIsSelectedCellInSelectedRange(state)
  }),
  { cancelTableSelection }
)(Popup);
